<template>
  <div id="createBlockDrawer">
    <b-card
      id="createBlockDrawerCard"
      class="blockDrawer large"
      :class="$store.state.drawer.createBlockDrawer.position"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="closeCreateBlockDrawer()"
        >
          <i class="far fa-times"></i>
        </a>
        <h4 v-if="$store.state.display.editMode == false" id="blockTitle">Create Block(s)</h4>
        <h4 v-if="$store.state.display.editMode" id="blockTitle">Update Block</h4>
      </template>
      <form @submit.prevent class="mb-5 pb-5">
        <div class="drawer-dimmer" v-if="$store.state.loadingMap"></div>
        <!-- CHECK FOR A PROPOSED HEADER NAMED "NAME"-->
        <b-alert
          v-if="proposedHeaders.some(e => e.name === 'name')"
          show
        >The blocks being imported include the header 'name'. Values from this header will automatically be assigned to block names.</b-alert>

        <!-- BLOCK NAME -->
        <div class="form-group">
          <label for="blockName"><strong>Block Name</strong></label>
          <input
            v-model="block.name"
            id="blockName"
            class="form-control"
          />
        </div><!-- /.form-group -->

        <!-- Description -->
        <div class="form-group">
          <label><strong>Description</strong></label>
          <textarea
            v-model="block.description"
            class="form-control"
          />
        </div><!-- /.form-group -->

        <div v-for="(groupedFields, group) in groupedBlockFields" v-bind:key="group">
          <h5 class="text-underline" v-if="group !== 'Others'">{{ group }}</h5> <!-- Group Title -->

          <!-- FIELDS -->
          <div
            v-for="(field, i) in groupedFields"
            v-bind:key="i"
            class="form-group mt-4 mb-4"
          >
            <div v-if="!field.hidden">
              <label><strong>{{ field.label }}</strong><span v-if="field.type == 'Number' && field.min && field.max && field.step"><strong>:</strong> {{ block[field.machine_name] }} <span v-if="field.suffix">{{ field.suffix }}</span></span></label>

              <div v-if="field.isLogEnabled" class="card p-2 mb-2">
                <p class="mb-2"><small><strong>Latest:</strong> {{ getLatestLogDisplay(field) }}{{ (field.type === 'Number' && field.suffix) ? ' ' + field.suffix : '' }}</small> <b-button v-if="block.id" size="sm" variant="outline-secondary" class="ml-2" @click="openLogHistory(field)">View logs</b-button></p>
                <label class="small mb-1">Date for this entry</label>
                <b-form-datepicker
                  v-model="getLogDraft(field.machine_name, field).logDate"
                  class="mb-2"
                  reset-button
                />
                <label class="small mb-1">Time for this entry</label>
                <b-form-timepicker
                  v-model="getLogDraft(field.machine_name, field).logTime"
                  locale="en"
                  class="mb-2"
                  reset-button
                />
                <template v-if="getLogValueType(field) === 'datetime'">
                  <b-form-datepicker
                    v-model="getLogDraft(field.machine_name, field).date"
                    class="mb-2"
                    reset-button
                    placeholder="Date"
                  />
                  <b-form-timepicker
                    v-if="field.includeTime"
                    v-model="getLogDraft(field.machine_name, field).time"
                    locale="en"
                    class="mb-2"
                    reset-button
                  />
                  <b-button size="sm" class="mr-1 mb-2" @click="setLogDraftToNow(field)">{{ field.includeTime ? 'Set to Now' : 'Set to Today' }}</b-button>
                </template>
                <input
                  v-else-if="getLogValueType(field) === 'text'"
                  v-model="getLogDraft(field.machine_name, field).value"
                  class="form-control mb-2"
                  placeholder="Add a new log entry"
                />
                <template v-else-if="getLogValueType(field) === 'number'">
                  <input
                    v-if="!field.min || !field.max || !field.step"
                    v-model="getLogDraft(field.machine_name, field).value"
                    class="form-control mb-2"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    placeholder="Add a new numeric entry"
                  />
                  <div v-else class="mb-2">
                    <b-form-input
                      v-model="getLogDraft(field.machine_name, field).value"
                      type="range"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                    />
                    <p class="mb-0 mt-1"><strong>{{ getLogDraft(field.machine_name, field).value }}</strong><span v-if="field.suffix"> {{ field.suffix }}</span></p>
                  </div>
                  <span v-if="field.suffix && (!field.min || !field.max || !field.step)" class="small text-muted">{{ field.suffix }}</span>
                </template>
                <b-form-select
                  v-else-if="getLogValueType(field) === 'boolean'"
                  v-model="getLogDraft(field.machine_name, field).value"
                  class="mb-2"
                  :options="[{ value: '', text: 'Select' }, { value: true, text: 'True' }, { value: false, text: 'False' }]"
                />
                <div v-else-if="getLogValueType(field) === 'select'" class="mb-2">
                  <span
                    v-for="(option, oi) in field.options"
                    :key="oi"
                    class="mr-1"
                  >
                    <b-btn
                      :variant="getLogDraft(field.machine_name, field).value === option ? 'success' : 'outline-secondary'"
                      size="sm"
                      @click="getLogDraft(field.machine_name, field).value = option"
                    >{{ option }}</b-btn>
                  </span>
                </div>
                <div v-else-if="getLogValueType(field) === 'image'" class="mb-2">
                  <p class="small text-muted mb-1">Add one or more photos for this log entry (e.g. updated shots from a revisit). Save block to add the entry.</p>
                  <ImageUpload
                    :key="'log-img-' + field.machine_name + '-' + (getLogDraft(field.machine_name, field).value && getLogDraft(field.machine_name, field).value.length ? 'set' : 'empty')"
                    :blockUpload="true"
                    :mediaObject="getLogDraft(field.machine_name, field).value || []"
                    v-on:mediaDelivery="logImageDelivered($event, field)"
                  />
                </div>
                <input
                  v-model="getLogDraft(field.machine_name, field).notes"
                  class="form-control"
                  placeholder="Optional note"
                />
              </div>

              <input
                v-if="field.type == 'Text' && !field.isLogEnabled"
                v-model="block[field.machine_name]"
                class="form-control"
              />

              <input
                v-if="field.type == 'Number' && !field.step && !field.isLogEnabled"
                v-model="block[field.machine_name]"
                class="form-control"
                type="number"
              />

              <div
                v-if="field.type == 'Number' && field.min && field.max && field.step && !field.isLogEnabled"
              >
                <b-form-input
                  v-model="block[field.machine_name]"
                  type="range"
                  :min="field.min"
                  :max="field.max"
                  :step="field.step"
                ></b-form-input>
              </div><!-- v-if -->

              <div
                v-if="field.type == 'Select' && showSelects && !field.isLogEnabled"
              >
                <span
                  v-for="(option, i) in field.options"
                  v-bind:key="i"
                  class="mr-1"
                >
                  <b-btn
                    v-if="block[field.machine_name] === option"
                    @click="selectOption(field.machine_name, option)"
                    variant="success"
                  >{{ option }}</b-btn>
                  <b-btn
                    v-else
                    @click="selectOption(field.machine_name, option)"
                  >{{ option }}</b-btn>
                </span><!-- /OPTIONS -->
              </div><!-- /IF Select -->

              <!-- DATE AND TIMES -->
              <div
                v-if="field.type === 'Date and Time' && !hideTimeFields && block[field.machine_name] && !field.isLogEnabled"
              >
                <b-form-datepicker
                  reset-button
                  @input="setDateTime(field.machine_name, 'date', $event)"
                  class="mb-2"
                  v-model="block[field.machine_name].date"
                ></b-form-datepicker>
                <b-form-timepicker
                  reset-button
                  v-if="field.includeTime"
                  @input="setDateTime(field.machine_name, 'time', $event)"
                  locale="en"
                  v-model="block[field.machine_name].time"
                ></b-form-timepicker>
                <b-button
                  @click="setToNow(field.machine_name)"
                  class="mt-2"
                  v-if="field.includeTime"
                >Set to Now</b-button>
                <b-button
                  @click="setToNow(field.machine_name)"
                  class="mt-2"
                  v-if="!field.includeTime"
                >Set to Today</b-button>
              </div><!-- /IF Date and Time -->

              <ImageUpload
                v-if="field.type == 'Image' && !field.isLogEnabled"
                :blockUpload="true"
                :mediaObject="block[field.machine_name]"
                v-on:mediaDelivery="imagesDelivered($event, field.machine_name)"
              />
            </div><!-- /IF NOT HIDDEN -->
          </div><!-- /.form-group -->
        </div><!-- /GROUPS -->

        <div class="form-group">
          <label><strong>Import GIS File</strong></label>
          <small class="form-text text-muted mb-2">You can use a zipped shape file or a GeoJson file to import this block's footprint. You may also simply draw the footprint on the map.</small>
          <input
            type="file"
            id="inputGISFile"
            name="inputGISFile"
            value="Import"
            class="mb-2"
            accept=".txt,.json,.geojson,.zip"
          />
        </div><!-- /.form-group -->

        <!-- VERSION CONTROL -->
        <div
          v-if="$store.state.display.editMode"
          class="form-group mt-4 mb-4"
        >
          <label><strong>Revision Message</strong><br><small>Use this optional field to log what you've changed in this version of this block. This helps track changes over time.</small></label>
          <input
            v-model="revisionMessage"
            class="form-control"
          />
        </div><!-- /.form-group -->

        <div
          class="list-group"
          v-if="proposedHeaders.length > 0"
        >
          <label><strong>Headers to Import</strong></label>
          <p class="text-right"><small>Select a Header for Block Name</small></p>
          <div
            class="list-container"
            v-for="(proposedHeader, index) in proposedHeaders"
            v-bind:key="index"
          >
            <b-btn
              @click="setNameField(index)"
              v-if="showNameField && !proposedHeaders[index].nameField"
              class="float-right select-button"
              size="sm"
              variant="secondary"
            ><i class="fas fa-circle"></i></b-btn>
            <b-btn
              @click="setNameField(index)"
              v-if="showNameField && proposedHeaders[index].nameField"
              class="float-right select-button"
              size="sm"
              variant="success"
            ><i class="fas fa-check-circle"></i></b-btn>
            <div class="list-group-item mb-2">
              <i class="fas fa-minus-circle text-danger clickable" @click="proposedHeaders.splice(index, 1)"></i> {{ proposedHeader.name }} ({{ proposedHeader.type }})
            </div><!-- /.list-group-item -->
          </div><!-- /.list-container -->
        </div><!-- /.list-group -->

        <div class="drawer-footer">
          <button
            v-if="$store.state.display.editMode == false && !$store.state.loadingMap"
            @click="createBlock()"
            class="btn btn-primary mr-1 w-50"
          >
            Submit
          </button>
          <button
            v-if="$store.state.display.editMode"
            @click="createBlock()"
            class="btn btn-primary mr-1 w-50"
          >
            Save
          </button>
          <button
            v-if="$store.state.display.editMode"
            @click="confirmDelete = true"
            class="btn btn-danger mr-1"
          >
            Delete
          </button>
          <button
            v-if="!$store.state.loadingMap"
            @click="closeCreateBlockDrawer()"
            class="btn btn-secondary"
          >
            Cancel
          </button>
        </div><!-- /.float-right -->
      </form>
    </b-card>
    <Confirm
      v-if="confirmDelete"
      text="DANGER! Are you sure you want to delete this block? This cannot be undone!"
      v-on:confirmed="deleteBlock($event)"
    />
    <LogHistoryModal
      :show="showLogModal"
      entity-type="block"
      :entity-id="block && block.id"
      :farm-id="(farm && farm.id) || (block && block.farmId)"
      :header="logHistoryHeader"
      :can-edit="$store.state.role === 'Administrator' || $store.state.role === 'Editor'"
      @updated="onLogUpdated"
      @close="showLogModal = false"
    />
  </div><!-- /#createBlockDrawer -->
</template>

<script>
import Confirm from '../Confirm'
import ImageUpload from '../collector/ImageUpload'
import LogHistoryModal from '../LogHistoryModal'
import 'firebase/compat/firestore'
import firebase from 'firebase/compat/app'
import shp from 'shpjs'
import { deriveHeaders } from '@/lib/deriveHeaders'
import { appendBlockLogEntry } from '../../services/HeaderLogService'
const fb = require('../../firebaseConfig.js')
const turf = require('@turf/turf')

export default {
  data () {
    return {
      confirmDelete: false,
      loadingFromFile: false,
      showSelects: true,
      proposedHeaders: [],
      showNameField: false,
      nameHeader: false,
      revisionMessage: '',
      hideTimeFields: true,
      logDrafts: {},
      showLogModal: false,
      logHistoryHeader: null
    }
  },
  components: {
    Confirm,
    ImageUpload,
    LogHistoryModal
  },
  computed: {
    block: {
      get () {
        return this.$store.state.block
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'block', value: value })
      }
    },
    farm: {
      get () {
        return this.$store.state.farm
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'farm', value: value })
      }
    },
    drawControlsObject: {
      get () {
        return this.$store.state.drawControlsObject
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'drawControlsObject', value: value })
      }
    },
    groupedBlockFields () {
      if (this.$store.state.farm.blockFields) {
        return this.$store.state.farm.blockFields.reduce((grouped, field) => {
          const group = field.group || 'Others'
          if (!grouped[group]) {
            grouped[group] = []
          }
          grouped[group].push(field)
          return grouped
        }, {})
      } else {
        return []
      }
    }
  },
  created () {
    this.init()
  },
  methods: {
    getLogDraft (machineName, field) {
      if (!this.logDrafts[machineName]) {
        const d = new Date()
        const logDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
        const logTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0')
        const base = { notes: '', logDate, logTime }
        const type = field && (field.logValueType || this.deriveLogValueType(field.type))
        if (type === 'datetime') {
          this.$set(this.logDrafts, machineName, { ...base, date: '', time: field && field.includeTime ? '' : '00:00:00' })
        } else if (type === 'image') {
          this.$set(this.logDrafts, machineName, { ...base, value: [] })
        } else {
          this.$set(this.logDrafts, machineName, { ...base, value: '' })
        }
      }
      return this.logDrafts[machineName]
    },
    deriveLogValueType (headerType) {
      const t = (headerType || '').toLowerCase()
      if (t === 'number') return 'number'
      if (t === 'text') return 'text'
      if (t === 'select') return 'select'
      if (t === 'date and time') return 'datetime'
      if (t === 'image') return 'image'
      return 'text'
    },
    getLogValueType (field) {
      return field.logValueType || this.deriveLogValueType(field.type)
    },
    getLatestLogValue (field) {
      if (!field || !field.machine_name) return null
      const latest = this.block.latestLogs && this.block.latestLogs[field.machine_name]
      if (!latest || latest.value === undefined || latest.value === null) return null
      return latest.value
    },
    getLatestLogDisplay (field) {
      const raw = this.getLatestLogValue(field)
      if (raw === null || raw === undefined) return '—'
      if (this.getLogValueType(field) === 'datetime' && raw && typeof raw.toDate === 'function') {
        const d = raw.toDate()
        return field.includeTime ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : d.toLocaleDateString(undefined, { dateStyle: 'short' })
      }
      if (this.getLogValueType(field) === 'image' && raw) {
        if (Array.isArray(raw)) {
          const count = raw.filter(img => img && typeof img === 'object' && (img.path || img.url || img.small || img.large)).length
          if (!count) return '—'
          return count === 1 ? '1 image' : `${count} images`
        }
        if (typeof raw === 'object') return '1 image'
      }
      if (raw === '') return '—'
      return raw
    },
    setLogDraftToNow (field) {
      const draft = this.getLogDraft(field.machine_name, field)
      const now = new Date()
      draft.date = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0')
      draft.time = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0') + ':' + String(now.getSeconds()).padStart(2, '0')
    },
    logImageDelivered (payload, field) {
      const draft = this.getLogDraft(field.machine_name, field)
      draft.value = Array.isArray(payload) ? payload : []
    },
    getPendingBlockLogEntries () {
      const entries = []
      ;(this.farm.blockFields || []).forEach(field => {
        if (!field.isLogEnabled) return
        const draft = this.getLogDraft(field.machine_name, field)
        const logDate = draft.logDate || (() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') })()
        const type = this.getLogValueType(field)
        let normalizedValue
        if (type === 'datetime') {
          if (!draft.date) return
          const time = (draft.time || '00:00:00').substring(0, 8)
          const date = new Date(draft.date + 'T' + time)
          normalizedValue = firebase.firestore.Timestamp.fromDate(date)
        } else if (type === 'image') {
          if (!Array.isArray(draft.value) || !draft.value.length) return
          normalizedValue = draft.value
        } else {
          if (draft.value === '' || draft.value === undefined || draft.value === null) return
          normalizedValue = draft.value
          if (type === 'number') {
            normalizedValue = Number(draft.value)
            if (isNaN(normalizedValue)) return
          }
        }
        entries.push({
          header: field,
          value: normalizedValue,
          notes: draft.notes || '',
          logDate,
          logTime: draft.logTime
        })
      })
      return entries
    },
    getLogSelectOptions (field) {
      const options = [{ value: '', text: 'Select' }]
      ;(field.options || []).forEach(option => {
        options.push({ value: option, text: option })
      })
      return options
    },
    appendPendingBlockLogs (blockId) {
      const entries = this.getPendingBlockLogEntries()
      if (!entries.length || !blockId) return Promise.resolve()
      return Promise.all(entries.map(entry => {
        const timeStr = (entry.logTime || '12:00:00').substring(0, 8)
        const timestamp = entry.logDate ? new Date(entry.logDate + 'T' + timeStr) : new Date()
        return appendBlockLogEntry({
          blockId,
          farmId: this.farm.id || this.block.farmId || '',
          header: entry.header,
          value: entry.value,
          notes: entry.notes,
          userId: fb.auth.currentUser && fb.auth.currentUser.uid,
          timestamp
        }).then(() => {
          this.block[entry.header.machine_name] = entry.value
          if (!this.block.latestLogs) {
            this.$set(this.block, 'latestLogs', {})
          }
          this.$set(this.block.latestLogs, entry.header.machine_name, {
            value: entry.value,
            timestamp: firebase.firestore.Timestamp.now(),
            notes: entry.notes,
            userId: fb.auth.currentUser && fb.auth.currentUser.uid
          })
          const draft = this.getLogDraft(entry.header.machine_name, entry.header)
          const d = new Date()
          const logDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
          const logTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0')
          draft.notes = ''
          draft.logDate = logDate
          draft.logTime = logTime
          if (this.getLogValueType(entry.header) === 'datetime') {
            draft.date = ''
            draft.time = entry.header.includeTime ? '' : '00:00:00'
          } else if (this.getLogValueType(entry.header) === 'image') {
            draft.value = []
          } else {
            draft.value = ''
          }
        })
      }))
    },
    openLogHistory (field) {
      this.logHistoryHeader = field
      this.showLogModal = true
    },
    async onLogUpdated () {
      if (this.block && this.block.id) {
        const doc = await fb.blocksCollection.doc(this.block.id).get()
        if (doc.exists) this.$store.commit('setStateProperty', { property: 'block', value: { ...doc.data(), id: doc.id } })
      }
      if (this.$store.state.farm && this.$store.state.farm.id) {
        await fb.farmsCollection.doc(this.$store.state.farm.id).update({ recompileBlocks: true })
      }
    },
    setToNow (machineName) {
      this.hideTimeFields = true
      function getToday () {
        let today = new Date()
        const dd = String(today.getDate()).padStart(2, '0')
        const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
        const yyyy = today.getFullYear()

        today = yyyy + '-' + mm + '-' + dd
        return today
      }
      function getCurrentTime () {
        const now = new Date()
        const hh = String(now.getHours()).padStart(2, '0')
        const mm = String(now.getMinutes()).padStart(2, '0')
        const ss = String(now.getSeconds()).padStart(2, '0')

        const time = hh + ':' + mm + ':' + ss
        return time
      }
      this.block[machineName] = {
        date: getToday(),
        time: getCurrentTime()
      }
      setTimeout(() => {
        this.hideTimeFields = false
      }, 1)
    },
    setDateTime (machineName, option, event) {
      function getToday () {
        let today = new Date()
        const dd = String(today.getDate()).padStart(2, '0')
        const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
        const yyyy = today.getFullYear()

        today = yyyy + '-' + mm + '-' + dd
        return today
      }
      if (!this.block[machineName]) {
        this.block[machineName] = {
          date: false,
          time: false
        }
      }
      if (option === 'date') {
        this.block[machineName].date = event
      }
      if (option === 'time') {
        this.block[machineName].time = event
        if (!this.block[machineName].date) {
          this.block[machineName].date = getToday()
        }
      }
    },
    setNameField (index) {
      this.showNameField = false
      this.nameHeader = this.proposedHeaders[index].name
      this.proposedHeaders.forEach((proposedHeader, i) => {
        if (i === index) {
          proposedHeader.nameField = true
        } else {
          proposedHeader.nameField = false
        }
      })
      setTimeout(() => {
        this.showNameField = true
      }, 10)
    },
    selectOption (field, option) {
      this.showSelects = false
      this.block[field] = option
      this.showSelects = true
    },
    imagesDelivered (e, field) {
      this.block[field] = e
    },
    deleteBlock (confirm) {
      if (confirm) {
        this.confirmDelete = false
        if (this.farm.blockFields) {
          this.farm.blockFields.forEach((field, i) => {
            if (field.type === 'Image') {
              this.block[field.machine_name].forEach((imageItem, i) => {
                firebase.storage().ref(imageItem.path).delete()
                firebase.storage().ref(imageItem.small).delete()
                firebase.storage().ref(imageItem.medium).delete()
                firebase.storage().ref(imageItem.large).delete()
              })
            }
          })
        }
        return fb.blocksCollection.doc(this.$store.state.block.id).delete().then(() => {
          return fb.farmsCollection.doc(this.farm.id).update({
            recompileBlocks: true
          })
        }).then(() => {
          this.closeCreateBlockDrawer()
        }).catch(err => {
          console.log(err.message)
        })
      } else {
        this.confirmDelete = false
        return false
      }
    },
    createBlock () {
      // Check that required fields are all set
      if (!this.block.footprint) {
        // alert('You must either draw this block on the map or upload a footprint GIS file before saving this block!')
        this.$store.commit('setAlert', { active: true, message: 'You must either draw this block on the map or upload a footprint GIS file before saving this block!', level: 'alert-warning', timeout: 5000 })
        return false
      }

      let formComplete = true
      if (this.farm.blockFields) {
        this.farm.blockFields.forEach((field, i) => {
          let badNumber = false
          if (field.isLogEnabled) {
            const draft = this.getLogDraft(field.machine_name, field)
            const hasExistingLatest = this.getLatestLogValue(field) != null
            const type = this.getLogValueType(field)
            if (type === 'number' && draft.value !== '' && draft.value !== null && draft.value !== undefined) {
              if (isNaN(Number(draft.value))) {
                badNumber = true
              } else {
                draft.value = Number(draft.value)
              }
            }
          let logFilled = false
          if (type === 'datetime') logFilled = !!draft.date
          else if (type === 'image') logFilled = Array.isArray(draft.value) && draft.value.length > 0
          else logFilled = draft.value !== '' && draft.value !== undefined && draft.value !== null && !badNumber
            if (field.required && !hasExistingLatest && !logFilled) {
              this.$store.commit('setAlert', { active: true, message: 'Required field missing: ' + field.label, level: 'alert-warning', timeout: 5000 })
              formComplete = false
            }
            return
          }
          if (field.type === 'Number') {
            if (isNaN(this.block[field.machine_name])) {
              badNumber = true
            } else {
              const oldVal = this.block[field.machine_name]
              const newVal = Number(oldVal)
              this.block[field.machine_name] = newVal
            }
          }

          // DEAL WITH DATES AND TIMES
          if (field.type === 'Date and Time') {
            if (!this.block[field.machine_name]) {
              this.block[field.machine_name] = {}
            }
            if (this.block[field.machine_name].date) {
              if (!this.block[field.machine_name].time) {
                this.block[field.machine_name].time = '00:00:00'
              }
              const dateString = `${this.block[field.machine_name].date}T${this.block[field.machine_name].time}`
              const date = new Date(dateString)
              this.block[field.machine_name] = firebase.firestore.Timestamp.fromDate(date)
            }
          }
          // END DATES AND TIMES

          if (field.required) {
            if (this.block[field.machine_name] === undefined || this.block[field.machine_name].length < 1 || badNumber) {
              this.$store.commit('setAlert', { active: true, message: 'Required field missing: ' + field.label, level: 'alert-warning', timeout: 5000 })
              formComplete = false
            }
          }
        })
      } else {
        this.farm.blockFields = []
      }
      if (!formComplete) {
        return false
      }

      this.$store.commit('setMapLoader', true)
      if (this.$store.state.display.editMode === false) {
        // Loop through multi-polygon blocks and generate multiple blocks
        const parsedGISData = JSON.parse(this.block.footprint)
        if (parsedGISData.features.length > 1000) {
          // Limit blocks to 1000
          this.$store.commit('setAlert', { active: true, message: 'You cannot create more than 1000 blocks at a time. Please reach out to support if you need help creating many blocks.', level: 'alert-warning' })
          this.$store.commit('setMapLoader', false)
        } else {
          this.proposedHeaders.forEach(header => {
            const self = this
            let fieldExists = false
            self.farm.blockFields.forEach(blockField => {
              if (blockField.machine_name === header.name) {
                fieldExists = true
              }
            })
            if (!fieldExists) {
              self.farm.blockFields.push({
                label: header.name,
                type: header.type,
                machine_name: header.name,
                min: false,
                max: false,
                options: [],
                step: false,
                suffix: false,
                required: false
              })
            }
          })

          parsedGISData.features.forEach((feature, i) => {
            // const headers = Object.keys(feature.properties)
            const headers = []
            if (!this.farm.blockFields) {
              this.farm.blockFields = []
            }
            this.farm.blockFields.forEach((header, i) => {
              headers.push(header)
            })
            const thisFootprint = { type: 'FeatureCollection', features: [feature] }
            // I have to strip properties from this without impacting the feature so I need a copy
            const thisFootprintCopy = JSON.parse(JSON.stringify(thisFootprint))
            thisFootprintCopy.features.forEach(featureCopy => {
              featureCopy.properties = {}
            })
            const thisFootprintString = JSON.stringify(thisFootprintCopy)

            // Naming precedence:
            //   1. an explicit `name` property on the feature
            //   2. the property the user picked as the name field (import)
            //   3. the user-typed Block Name — used as-is for a single
            //      block, or as the base for an iterated series when
            //      several blocks are drawn/imported at once
            //      ("Vineyard A" -> "Vineyard A 1", "Vineyard A 2", ...)
            //   4. a generic "Block N" fallback when no name was given
            const userBase = (this.block.name && String(this.block.name).trim())
              ? String(this.block.name).trim()
              : ''
            let thisName
            if (feature.properties.name) {
              thisName = feature.properties.name
            } else if (this.nameHeader && feature.properties[this.nameHeader]) {
              thisName = feature.properties[this.nameHeader]
            } else if (parsedGISData.features.length === 1) {
              thisName = userBase || 'Block'
            } else if (userBase) {
              thisName = userBase + ' ' + (i + 1)
            } else {
              thisName = 'Block ' + (i + 1)
            }
            // Get this block area
            const thisArea = turf.area(thisFootprint)
            const blockObject = this.block

            headers.forEach(headerKey => {
              const restrictedTerms = [
                'createdBy',
                'createdOn',
                'updatedBy',
                'updatedByName',
                'updatedOn',
                // 'name',
                'farmId',
                'blockId',
                'footprint',
                'area'
              ]
              if (!restrictedTerms.includes(headerKey.label)) {
                if (headerKey.type === 'Number') {
                  blockObject[headerKey.label] = feature.properties[headerKey.label] || 0
                } else {
                  blockObject[headerKey.label] = feature.properties[headerKey.label] || ''
                }
              }
            })

            blockObject.name = thisName || ''
            blockObject.description = this.block.description || ''
            blockObject.createdBy = this.$store.state.currentUser.uid || ''
            blockObject.updatedBy = this.$store.state.currentUser.uid || ''
            blockObject.updatedByName = this.$store.state.userProfile.firstName + ' ' + this.$store.state.userProfile.lastName || this.$store.state.userProfile.email
            blockObject.farmId = this.farm.id || ''
            blockObject.footprint = thisFootprintString || ''
            blockObject.area = thisArea || ''
            blockObject.createdOn = new Date()
            blockObject.updatedOn = new Date()
            blockObject.revisionMessage = 'Initial block created.'

            // Create the block
            fb.blocksCollection.add(blockObject).then((docRef) => {
              return this.appendPendingBlockLogs(docRef.id).then(() => docRef.id)
            }).then(() => {
              const parsedGISDataLength = parsedGISData.features.length - 1
              if (i === parsedGISDataLength) {
                console.log('Update Farm in 3 seconds')
                setTimeout(() => {
                  fb.farmsCollection.doc(this.$store.state.farm.id).update({
                    recompileBlocks: true,
                    blockFields: this.farm.blockFields || []
                  }).then(() => {
                    console.log('Farm Updated')
                  })
                }, 3000)
                setTimeout(() => {
                  this.closeCreateBlockDrawer()
                }, 4000)
              }
            }).catch(err => {
              console.log(err)
            })
          })
        }
      }
      if (this.$store.state.display.editMode === true) {
        // Check to make sure there is no more than one polygon
        const parsedGISData = JSON.parse(this.$store.state.block.footprint)
        if (parsedGISData.features.length > 1) {
          alert('Warning: You cannot add new blocks while editing a block. To add new blocks to your farm, select "Create Block" from your farm map.')
          return false
        }
        const thisArea = turf.area(parsedGISData)
        const blockObject = this.block
        blockObject.name = this.block.name || ''
        blockObject.description = this.block.description || ''
        blockObject.updatedOn = new Date()
        blockObject.updatedBy = this.$store.state.currentUser.uid || ''
        blockObject.updatedByName = this.$store.state.userProfile.firstName + ' ' + this.$store.state.userProfile.lastName || this.$store.state.userProfile.email
        blockObject.farmId = this.farm.id || ''
        blockObject.footprint = this.block.footprint || ''
        blockObject.area = thisArea || ''
        // SAVE REVISION FROM CURRENT DATA (REFETCH FIRST)
        fb.blocksCollection.doc(this.block.id).get().then(blockDoc => {
          const blockData = blockDoc.data()
          
          // Create a clean copy for revision (without id field to avoid conflicts)
          const revisionData = { ...blockData }
          delete revisionData.id
          
          return fb.blocksCollection.doc(this.block.id).collection('revisions').add(revisionData)
        }).then(() => {
          // UPDATE THE BLOCK
          blockObject.revisionMessage = this.revisionMessage
          console.log(blockObject)
          return fb.blocksCollection.doc(this.block.id).update(blockObject)
        }).then(() => {
          return this.appendPendingBlockLogs(this.block.id)
        }).then(() => {
          return fb.farmsCollection.doc(this.$store.state.farm.id).update({
            recompileBlocks: true
          })
        }).then(ref => {
          this.closeCreateBlockDrawer()
        }).catch(err => {
          console.log(err)
        })
      }
    },
    init () {
      if (this.$store.state.display.editMode === false && this.loadingFromFile === false) {
        this.block = {}
      }
      this.hideTimeFields = true
      // Formatting function for date
      function formatDate (dateObject) {
        const dd = String(dateObject.getDate()).padStart(2, '0')
        const mm = String(dateObject.getMonth() + 1).padStart(2, '0') // January is 0
        const yyyy = dateObject.getFullYear()

        return yyyy + '-' + mm + '-' + dd
      }

      // Formatting function for time
      function formatTime (dateObject) {
        const hh = String(dateObject.getHours()).padStart(2, '0')
        const mm = String(dateObject.getMinutes()).padStart(2, '0')
        const ss = String(dateObject.getSeconds()).padStart(2, '0')

        return hh + ':' + mm + ':' + ss
      }
      // MAKE SURE ANY DATE AND TIME ARE FORMETTED
      if (this.farm.blockFields) {
        this.farm.blockFields.forEach(field => {
          if (field.type === 'Date and Time') {
            // if (this.block[field.machine_name] && this.block[field.machine_name].seconds) {
            if (this.block[field.machine_name]) {
              // CONVERT BACK TO OBJECT (IF NOT ALREADY)
              if (this.block[field.machine_name].date === '') {
                // Now create the final object
                this.block[field.machine_name] = {
                  date: formatDate(new Date()),
                  time: formatTime(new Date())
                }
              } else {
                // Convert the Firestore timestamp to a JavaScript Date object.
                const dateObject = this.block[field.machine_name].toDate()

                // Now create the final object
                this.block[field.machine_name] = {
                  date: formatDate(dateObject),
                  time: formatTime(dateObject)
                }
              }
            } else {
              // this.setToNow(field.machine_name)
              this.block[field.machine_name] = {
                date: '',
                time: ''
              }
            }
          }
        })
      }
      setTimeout(() => {
        this.hideTimeFields = false
      }, 1)

      this.$store.dispatch('removeAllLayers')

      const map = this.$store.state.mapObject
      if (this.drawControlsObject) {
        map.removeControl(this.drawControlsObject)
      }
      // DRAW SETTINGS
      this.drawControlsObject = new MapboxDraw({ // eslint-disable-line
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        }
      })
      map.addControl(this.drawControlsObject)
      map.addLayer({
        id: 'blocksGreyedLayer',
        type: 'fill',
        source: {
          type: 'geojson',
          data: this.$store.state.blocksGeoJson
        },
        layout: {},
        paint: {
          'fill-color': 'black',
          'fill-opacity': 0.4,
          'fill-outline-color': 'black'
        }
      })

      // If a block footprint exists, add the existing footprint to draw
      if (this.block.footprint) {
        const footprint = JSON.parse(this.block.footprint)
        try {
          this.drawControlsObject.add(footprint)
          map.fitBounds(turf.bbox(footprint), {
            padding: 50,
            animate: false
          })
          this.gatherBlockHeaders()
        } catch (error) {
          this.$store.commit('setAlert', { active: true, message: 'WARNING: There was an issue importing your GeoJson file. We recommend refreshing your browser and trying again. If you continue to have this issue, ensure that your file is in the WGS84 coordinate system or try uploading a zipped shapefile instead.', level: 'alert-danger', timeout: 20000 })
        }
      }
      const self = this
      // When A Feature is created, write it to this.block.footprint
      map.on('draw.create', function (e) {
        self.block.footprint = JSON.stringify(self.drawControlsObject.getAll())
      })
      map.on('draw.delete', function (e) {
        self.block.footprint = JSON.stringify(self.drawControlsObject.getAll())
      })
      map.on('draw.update', function (e) {
        self.block.footprint = JSON.stringify(self.drawControlsObject.getAll())
      })

      // Handle uploaded GeoJson
      setTimeout(function () {
        document.getElementsByName('inputGISFile')[0].addEventListener('change', onFileSelect)
      }, 100)

      function onFileSelect (event) {
        // What is file's extension?
        const fileName = event.target.files[0].name
        const lastDot = fileName.lastIndexOf('.')
        const fileExt = fileName.substring(lastDot + 1)
        const reader = new FileReader()

        if (fileExt === 'geojson') {
          reader.onload = onGeoJsonLoad
          reader.readAsText(event.target.files[0])
        }

        if (fileExt === 'zip') {
          reader.onload = onShpLoad
          reader.readAsArrayBuffer(event.target.files[0])
        }
      }
      function onGeoJsonLoad (event) {
        const obj = JSON.parse(event.target.result)
        if (obj.crs) {
          delete obj.crs
        }
        const finalGeoJson = {
          type: 'FeatureCollection',
          features: []
        }
        // Push all polygons to finalGeoJson
        obj.features.forEach((feature, i) => {
          if (feature.geometry.type === 'MultiPolygon' && feature.geometry.coordinates[0] && feature.geometry.coordinates[0][0]) {
            const singlePolygon = {
              type: 'Feature',
              properties: feature.properties,
              geometry: {
                type: 'Polygon',
                coordinates: [feature.geometry.coordinates[0][0]]
              }
            }
            finalGeoJson.features.push(singlePolygon)
          }
          if (feature.geometry.type === 'Polygon') {
            finalGeoJson.features.push(feature)
          }
        })
        // Push any points to a polygon and push to the finalGeoJson
        const pointsPolygon = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[]]
          }
        }
        obj.features.forEach(point => {
          if (point.geometry.type === 'Point') {
            pointsPolygon.geometry.coordinates[0].push(point.geometry.coordinates)
          }
        })
        if (pointsPolygon.geometry.coordinates[0].length > 0) {
          // Push the first point again in order to complete the polygon
          pointsPolygon.geometry.coordinates[0].push(pointsPolygon.geometry.coordinates[0][0])
          finalGeoJson.features.push(pointsPolygon)
        }
        var objString = JSON.stringify(finalGeoJson)
        self.block.footprint = objString
        self.loadingFromFile = true
        self.init()
      }

      function onShpLoad (event) {
        shp(event.target.result).then(shpgeojson => {
          var objString = JSON.stringify(shpgeojson)
          self.block.footprint = objString
          self.loadingFromFile = true
          self.init()
        })
      }
    },
    closeCreateBlockDrawer () {
      const map = this.$store.state.mapObject
      map.removeControl(this.drawControlsObject)
      this.drawControlsObject = false
      // this.$store.commit('setDrawer', { name: 'createBlockDrawer', position: 'out' })
      // this.$store.commit('setStateProperty', { property: 'showCreateBlockDrawer', value: false })
      this.$store.commit('hideDrawer', 'createBlockDrawer')
      this.$store.dispatch('removeAllLayers')
      setTimeout(() => {
        // this.block = {}
        this.$store.dispatch('initFarmMap')
      }, 300)
    },
    gatherBlockHeaders () {
      // GATHER UP HEADERS
      const finalGeoJson = JSON.parse(this.block.footprint)

      // Look for special characters and replace with _
      finalGeoJson.features.forEach(item => {
        const itemHeaders = Object.keys(item.properties)
        itemHeaders.forEach(itemHeader => {
          const oldHeaderName = JSON.parse(JSON.stringify(itemHeader))
          const regex = /[&\/\\#,+()$~%.'":*?<>{}]/g // eslint-disable-line
          const testRegex = regex.test(itemHeader)
          if (testRegex) {
            const newHeaderName = itemHeader.replace(regex, '_')
            item.properties[newHeaderName] = item.properties[oldHeaderName]
            delete item.properties[oldHeaderName]
          }
        })
      })
      // Union across all imported features (a sparse first feature would
      // otherwise hide fields present on the rest).
      const headers = deriveHeaders(finalGeoJson)
      const restrictedTerms = [
        'createdBy',
        'createdOn',
        'updatedBy',
        'updatedByName',
        'updatedOn',
        // 'name',
        'farmId',
        'blockId',
        'footprint',
        'area'
      ]
      this.proposedHeaders = []
      this.showNameField = false
      this.nameHeader = false
      headers.forEach((header, me) => {
        if (!restrictedTerms.includes(header)) {
          let headerType = 'Number'
          if (isNaN(finalGeoJson.features[0].properties[header])) {
            headerType = 'Text'
          }
          const proposedHeader = {
            name: header,
            type: headerType,
            nameField: false
          }
          if (me === 0) {
            proposedHeader.nameField = true
            this.nameHeader = proposedHeader.name
          }
          this.proposedHeaders.push(proposedHeader)
        }
      })
      this.block.footprint = JSON.stringify(finalGeoJson)
      this.showNameField = true
    }
  }
}
</script>

<style scoped>
.select-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
}
.list-container {
  position: relative;
}
</style>
